<template>
  <div class="flex flex-col items-center">
    <div
      class="[&>div]:rounded-extra-small [&>div]:last:rounded-b-large [&>div]:first:rounded-t-large flex w-full flex-col gap-0.5"
    >
      <FeedbackTopicSetting
        :label="$t('teamFeedbackTask.useTopic', { topic: feedbackTopic.title })"
        :icon="feedbackTopic.icon!"
        :is-active="true"
        class="h-14!"
      >
        <BaseSwitch
          :value="isActive"
          @click.stop
          @value-updated="
            () => {
              emits('toggled')
            }
          "
      /></FeedbackTopicSetting>
      <template v-if="journalTask && isActive">
        <div class="bg-surface-container px-4 pt-2 pb-4">
          <BaseInputField
            v-model="journalTask.title"
            type="text"
            :label="$t('common.title')"
            class="bg-surface-container"
          />
        </div>
        <div class="bg-surface-container">
          <WysiwygTextField
            v-model="journalTask.message"
            :placeholder="$t('common.message') + ' (' + $t('common.optional') + ')'"
          />
        </div>
        <div class="bg-surface-container overflow-hidden">
          <SurveyStartDateForm v-model="journalTask.startDate" :label="$t('survey.startDate')" />
        </div>

        <SurveyRecurrenceForm
          v-model:is-recurring="journalTask.isRecurring"
          v-model:recurrence-interval="journalTask.recurrenceInterval"
          v-model:recurrence-frequency="journalTask.recurrenceFrequency"
          v-model:start-date="journalTask.startDate"
        />
      </template>
      <template v-else>
        <EvaluationPerspectiveGoalSetter
          v-for="perspective in topicPerspectives"
          :key="perspective.key"
          :is-active="isActive"
          :perspective="perspective"
          :label="labelForPerspective(perspective)"
          :number-of-feedbacks="getNumberOfFeedbacks(perspective)"
          @plus="adjustEvaluationTaskGoal(perspective, 'add')"
          @minus="adjustEvaluationTaskGoal(perspective, 'remove')"
          @update:number-of-feedbacks="setEvaluationTaskCount(perspective, $event)"
        />
      </template>
    </div>
    <div v-if="displayErrors.length > 0" class="w-full px-1 py-2">
      <p v-for="error in displayErrors" :key="error" class="text-body-medium text-error text-left">
        {{ error }}
      </p>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import BaseSwitch from '../../../core/components/base/switch/BaseSwitch.vue'
  import { FeedbackTopicType } from '@/core/models/feedback-topic'
  import {
    legacyTaskCountFieldForPerspective,
  } from '@/core/models/perspective-goal'
  import { perspectivesFromTopic, type Perspective } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import type { CompleteFeedbackTopic } from '../../../core/composables/use-complete-feedback-topics'
  import { type FeedbackTopicTask, type JournalTask } from '../composables/useAddCoacheesWizzard'
  import { SurveyRecurrenceFrequency } from '@/modules/start-survey/composables/use-start-survey'
  import FeedbackTopicSetting from './FeedbackTopicSetting.vue'
  import EvaluationPerspectiveGoalSetter from './EvaluationPerspectiveGoalSetter.vue'
  import WysiwygTextField from '@/core/components/base/wysiwyg/WysiwygTextField.vue'
  import SurveyStartDateForm from '@/modules/start-survey/components/SurveyStartDateForm.vue'
  import SurveyRecurrenceForm from '@/modules/start-survey/components/SurveyRecurrenceForm.vue'
  import BaseInputField from '@/core/components/base/form-fields/BaseInputField.vue'

  interface Props {
    feedbackTopic: CompleteFeedbackTopic
    task?: FeedbackTopicTask
    errors?: string[]
    error?: string
  }

  const props = defineProps<Props>()
  const { t } = useI18n()

  const emits = defineEmits<{
    (e: 'toggled'): void
  }>()

  const isActive = computed(() => props.task != undefined && props.task.isActive)
  const topicPerspectives = computed(() => perspectivesFromTopic(props.feedbackTopic))
  const journalTask = computed(() => {
    if (!props.task || props.task.feedbackTopicType !== FeedbackTopicType.Journal) {
      return undefined
    }
    const task = props.task as JournalTask
    task.isRecurring ??= false
    task.recurrenceFrequency ??= SurveyRecurrenceFrequency.Week
    task.recurrenceInterval ??= 1
    return task
  })
  const displayErrors = computed(() => {
    if (props.errors && props.errors.length > 0) {
      return props.errors
    }
    return props.error ? [props.error] : []
  })

  const labelForPerspective = (perspective: Perspective) =>
    resolvePerspectiveLabel(perspective.key, props.feedbackTopic.perspectives, {
      kind: 'title',
      selfLabel: t('perspectives.self.default'),
    })

  const getNumberOfFeedbacks = (perspective: Perspective) => {
    if (props.task?.feedbackTopicType != FeedbackTopicType.Evaluation) {
      return 0
    }
    const field = legacyTaskCountFieldForPerspective(perspective)
    return props.task?.[field] ?? 0
  }

  const setEvaluationTaskCount = (perspective: Perspective, value: number) => {
    if (!props.task || props.task.feedbackTopicType != FeedbackTopicType.Evaluation) {
      return
    }
    const field = legacyTaskCountFieldForPerspective(perspective)
    props.task[field] = Math.max(0, Math.trunc(Number(value)) || 0)
  }

  const adjustEvaluationTaskGoal = (perspective: Perspective, action: 'add' | 'remove') => {
    if (!props.task || props.task?.feedbackTopicType != FeedbackTopicType.Evaluation) {
      return
    }
    if (props.feedbackTopic.type != FeedbackTopicType.Evaluation) {
      return
    }
    const field = legacyTaskCountFieldForPerspective(perspective)
    if (action == 'add') {
      props.task[field]++
    } else if (props.task[field] > 0) {
      props.task[field]--
    }
  }
</script>
